import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLazyQuery, useQuery, gql, useMutation } from '@apollo/client';
import { Table, Spin, Button, message, Input, Modal } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { debounce } from 'lodash';
import ClientSortModal from './ClientSortModal';
import { AccountStatement } from '@PayTrustTypes/Account';

const STATUS_SUBSTITUTIONS: Record<string, string> = {
  FAILED: "MANUAL SORT",
};

const ACCOUNT_STATEMENTS_QUERY = gql`
  query GetAccountStatements($accountId: Float!) {
    accountStatementsByAccountId(accountId: $accountId) {
      id
      statementDate
      paymentTargetDate
      dueDate
      amountDue
      statementBalance
      paidAmount
      documentId
      hasAttachment
    }
  }
`;

const MOVE_TO_ACCOUNT_MUTATION = gql`
  mutation MoveToAccount(
    $unprocessedDocId: Float!,
    $uploadedByUserId: Float!,
    $clientId: Float!,
    $accountId: Float!,
    $statementDate: DateTime!,
    $paymentTargetDate: DateTime!,
    $dueDate: DateTime!,
    $statementBalance: Float!,
    $amountDue: Float!,
    $description: String
  ) {
    moveDocumentToAccount(
      unprocessedDocId: $unprocessedDocId,
      uploadedByUserId: $uploadedByUserId,
      clientId: $clientId,
      accountId: $accountId,
      statementDate: $statementDate,
      paymentTargetDate: $paymentTargetDate,
      dueDate: $dueDate,
      statementBalance: $statementBalance,
      amountDue: $amountDue,
      description: $description
    )
  }
`;


const MOVE_TO_UNSORTED_MUTATION = gql`
  mutation MoveToUnsorted($unprocessedDocId: Int!, $uploadedByUserId: Int!, $clientId: Int!, $description: String) {
    moveDocumentToUnsorted(unprocessedDocId: $unprocessedDocId, uploadedByUserId: $uploadedByUserId, clientId: $clientId, description: $description)
  }
`;

const GET_UNPROCESSED_DOCS_QUERY = gql`
  query GetUnprocessedDocs($page: Int!, $limit: Int!, $search: String) {
    unprocessedDocs(page: $page, limit: $limit, search: $search) {
      items {
        id
        status
        createdAt
        filename
        isBill
        serviceAddress
        amountDue
        dueDate
        customerName
        firstName
        middleName
        lastName
        vendor
        statementDate
        accountNumber
        blobId
        description
      }
      count
    }
  }
`;

const GET_DOWNLOAD_URL_QUERY = gql`
  query GetDownloadDocumentByBlobId($blobId: String!) {
    downloadDocumentByBlobId(blobId: $blobId)
  }
`;

const RESET_UNPROCESSED_DOCS_STATUS_MUTATION = gql`
  mutation ResetUnprocessedDocStatus($ids: [Int!]!) {
    resetUnprocessedDocumentStatus(ids: $ids)
  }
`;

const SEARCH_USERS_QUERY = gql`
  query SearchUsers($limit: Float!, $page: Float!, $search: String) {
    users(limit: $limit, page: $page, search: $search) {
      nodes {
        id
        firstName
        lastName
        status
      }
    }
  }
`;

const GET_USER_ACCOUNTS_QUERY = gql`
  query GetUserAccounts($userId: Float!) {
    user(id: $userId) {
      accounts {
        id
        name
        accountNumber
      }
      externalBankAccounts {
        id
        accountNickName
        accountNumber
      }
    }
  }
`;

interface UnprocessedDoc {
  id: string;
  status: string;
  filename: string;
  isBill: boolean;
  blobId: string;
  accountNumber: string;
  amountDue: number;
  customerName: string | null;
  description: string;
  dueDate: string;
  firstName: string;
  lastName: string | null;
  middleName: string;
  serviceAddress: string | null;
  statementDate: string;
  vendor: string;
}

interface UnprocessedDocsData {
  unprocessedDocs: {
    items: UnprocessedDoc[];
    count: number;
  };
}

interface UnprocessedDocsVars {
  limit: number;
  page: number;
  search: string;
}

interface UserSearchResult {
  id: string;
  firstName: string;
  lastName: string;
  status: 'Active' | 'Inactive';
}

interface UserAccount {
  id: string;
  name: string;
  accountNumber: string;
}

const UnprocessedDocsTable: React.FC = () => {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [unprocessedDocs, setUnprocessedDocs] = useState<UnprocessedDoc[]>([]);
  const [isPreviewVisible, setIsPreviewVisible] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');
  const [previewFileName, setPreviewFileName] = useState('');
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isConfirmationVisible, setIsConfirmationVisible] = useState(false);
  const [isMoving, setIsMoving] = useState(false);
  const [selectedDocId, setSelectedDocId] = useState<string | null>(null);
  const [searchUserTerm, setSearchUserTerm] = useState('');
  const [searchResults, setSearchResults] = useState<UserSearchResult[]>([]);
  const [selectedUser, setSelectedUser] = useState<UserSearchResult | null>(null);
  const [selectedAccount, setSelectedAccount] = useState<UserAccount | null>(null);
  const [moveToAccount] = useMutation(MOVE_TO_ACCOUNT_MUTATION);
  const [isMoveToAccountConfirmationVisible, setIsMoveToAccountConfirmationVisible] = useState(false);
  const [accountStatements, setAccountStatements] = useState([]);
  
  const [statementFormValues, setStatementFormValues] = useState<Partial<AccountStatement>>({
    statementDate: '',
    paymentTargetDate: '',
    dueDate: '',
    statementBalance: 0,
    amountDue: 0,
  });

  const statementFormRef = useRef<Partial<AccountStatement>>({
    statementDate: '',
    paymentTargetDate: '',
    dueDate: '',
    statementBalance: 0,
    amountDue: 0,
  });
  
  const [isStatementFormValid, setIsStatementFormValid] = useState(false);

  const [fetchUnprocessedDocs] = useLazyQuery<UnprocessedDocsData, UnprocessedDocsVars>(GET_UNPROCESSED_DOCS_QUERY);
  const [resetDocStatus] = useMutation(RESET_UNPROCESSED_DOCS_STATUS_MUTATION);
  const [getDownloadUrl] = useLazyQuery(GET_DOWNLOAD_URL_QUERY);
  const [moveToUnsorted] = useMutation(MOVE_TO_UNSORTED_MUTATION);
  const [getAccountStatements] = useLazyQuery(ACCOUNT_STATEMENTS_QUERY, {
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      setAccountStatements(data?.accountStatementsByAccountId || []);
    },
    onError: () => {
      message.error('Failed to load account statements');
      setAccountStatements([]);
    },
  });

  const [searchUsers, { data: userSearchData, error: userSearchError }] =
    useLazyQuery<{ users: { nodes: UserSearchResult[] } }, { limit: number; page: number; search: string }>(
      SEARCH_USERS_QUERY,
      { fetchPolicy: 'network-only' }
    );

  const { data: userAccountData } = useQuery(
    GET_USER_ACCOUNTS_QUERY,
    {
      variables: { userId: Number(selectedUser?.id) },
      skip: !selectedUser,
    }
  );

  useEffect(() => {
    if (userSearchData?.users?.nodes) {
      const sorted = [...userSearchData.users.nodes].sort((a, b) => {
        const aActive = a.status === 'Active' ? 1 : 0;
        const bActive = b.status === 'Active' ? 1 : 0;
        return bActive - aActive;
      });
      setSearchResults(sorted);
    }
  }, [userSearchData]);

  useEffect(() => {
    if (userSearchError) {
      message.error('Error loading users');
    }
  }, [userSearchError]);

  const debouncedSearchUsers = useCallback(
    debounce((value: string) => {
      if (value.length >= 3) {
        searchUsers({ variables: { search: value, limit: 100, page: 1 } });
      } else {
        setSearchResults([]);
      }
    }, 300),
    []
  );

  const handleStatementFormChange = (values: any, isValid: boolean) => {
    statementFormRef.current = values;
    setIsStatementFormValid(isValid);
  };
  

  const handleMoveToAccount = (formattedFilename?: string) => {
    setIsMoveToAccountConfirmationVisible(true);
  };

  const handlePreviewClick = async (blobId: string, filename: string) => {
    try {
      const { data } = await getDownloadUrl({ variables: { blobId } });
      if (data?.downloadDocumentByBlobId) {
        setPreviewUrl(data.downloadDocumentByBlobId);
        setIsPreviewVisible(true);
        setPreviewFileName(filename);
      }
    } catch {
      message.error('Error loading document preview');
    }
  };

  const handleSortClick = async (record: UnprocessedDoc) => {
    setSelectedDocId(record.id);
    const { data } = await getDownloadUrl({ variables: { blobId: record.blobId } });
    if (data?.downloadDocumentByBlobId) {
      setPreviewUrl(data.downloadDocumentByBlobId);
    }
    setIsModalVisible(true);
  };

  const handleModalCancel = () => {
    setIsModalVisible(false);
    setSearchUserTerm('');
    setSearchResults([]);
    setSelectedUser(null);
    setSelectedAccount(null);
  };

  const handleMoveToUnsorted = () => {
    setIsConfirmationVisible(true);
  };

  const performMoveToAccount = async () => {
    if (!selectedDocId || !selectedUser || !selectedAccount) return;
  
    setIsMoving(true);
    try {
      // Find the unprocessed document
      const unprocessedDoc = unprocessedDocs.find((doc: UnprocessedDoc) => doc.id === selectedDocId);
      if (!unprocessedDoc) {
        throw new Error('Document not found');
      }

      // Get the account
      const account = userAccountData?.user?.accounts.find((acc: UserAccount) => acc.id === selectedAccount.id);
      
      // Append "--moved to account [account_id]" to the existing description instead of overwriting it
      const currentDescription = unprocessedDoc.description || '';
      const updatedDescription = currentDescription ? `${currentDescription}--moved to account ${selectedAccount.id}` : `moved to account ${selectedAccount.id}`;

      // Helper function to convert DatePicker values (Moment.js/Day.js objects) to ISO strings
      const convertDateToISO = (dateValue: any): string => {
        if (!dateValue) {
          throw new Error('Date value is required');
        }
        
        // If it's already a Date object
        if (dateValue instanceof Date) {
          return dateValue.toISOString();
        }
        
        // If it's a Moment.js or Day.js object (has toDate method)
        if (dateValue && typeof dateValue.toDate === 'function') {
          return dateValue.toDate().toISOString();
        }
        
        // If it's a string, try to parse it
        if (typeof dateValue === 'string') {
          const parsedDate = new Date(dateValue);
          if (isNaN(parsedDate.getTime())) {
            throw new Error(`Invalid date string: ${dateValue}`);
          }
          return parsedDate.toISOString();
        }
        
        // Fallback: try to create a Date object
        const fallbackDate = new Date(dateValue);
        if (isNaN(fallbackDate.getTime())) {
          throw new Error(`Unable to convert date value: ${dateValue}`);
        }
        return fallbackDate.toISOString();
      };

      const values = {
        unprocessedDocId: parseInt(selectedDocId),
        uploadedByUserId: 1,
        clientId: parseInt(selectedUser.id),
        accountId: parseInt(selectedAccount.id),
        statementDate: convertDateToISO(statementFormRef.current.statementDate),
        paymentTargetDate: convertDateToISO(statementFormRef.current.paymentTargetDate),
        dueDate: convertDateToISO(statementFormRef.current.dueDate),
        statementBalance: Number(statementFormRef.current.statementBalance),
        amountDue: Number(statementFormRef.current.amountDue),
        description: updatedDescription,
      };
  
      await moveToAccount({ variables: values });
      message.success('Document successfully moved to Account.');
      handleModalCancel();
      await loadUnprocessedDocs();
    } catch (error: any) {
      message.error(`Failed to move document to Account: ${error?.message || error}`);
    } finally {
      setIsMoving(false);
      setIsMoveToAccountConfirmationVisible(false);
    }
  };
  
  
  const performMoveToUnsorted = async () => {
    if (!selectedDocId || !selectedUser) return;
  
    setIsMoving(true);
    try {
      // Find the unprocessed document to get the current description
      const unprocessedDoc = unprocessedDocs.find((doc: UnprocessedDoc) => doc.id === selectedDocId);
      if (!unprocessedDoc) {
        throw new Error('Document not found');
      }

      // Append "--moved to unsorted" to the existing description instead of overwriting it
      const currentDescription = unprocessedDoc.description || '';
      const updatedDescription = currentDescription ? `${currentDescription}--moved to unsorted` : 'moved to unsorted';

      await moveToUnsorted({
        variables: {
          unprocessedDocId: parseInt(selectedDocId),
          uploadedByUserId: 1,
          clientId: parseInt(selectedUser.id),
          description: updatedDescription,
        },
      });
      message.success('Document successfully moved to Unsorted.');
      handleModalCancel();
      await loadUnprocessedDocs();
    } catch {
      message.error('Failed to move document to Unsorted.');
    } finally {
      setIsMoving(false);
      setIsConfirmationVisible(false); // ✅ Always close confirmation modal
    }
  };
  

  const handleUserClick = async (unprocessedDocId: string) => {
    try {
      await resetDocStatus({ variables: { ids: [+unprocessedDocId] } });
      message.success(`Status reset for document ${unprocessedDocId}`);
      await loadUnprocessedDocs();
    } catch {
      message.error("Error resetting document status");
    }
  };

  const handleTableChange = (pagination: any) => {
    setPage(pagination.current);
    setPageSize(pagination.pageSize);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
  };

  const handleRefreshClick = () => {
    loadUnprocessedDocs();
  };

  const loadUnprocessedDocs = useCallback(() => {
    setLoading(true);
    fetchUnprocessedDocs({
      variables: { limit: pageSize, page, search: debouncedSearch },
      fetchPolicy: "network-only",
      onCompleted: (data) => {
        const processedItems = data.unprocessedDocs.items.map((item: UnprocessedDoc) => {
          if (item.firstName || item.middleName || item.lastName) {
            const fullName = [item.firstName, item.middleName, item.lastName].filter(Boolean).join(' ');
            return { ...item, customerName: fullName };
          }
          return item;
        });
        setUnprocessedDocs(processedItems);
        setTotal(data.unprocessedDocs.count);
        setLoading(false);
      },
      onError: () => {
        message.error("Error loading unprocessed documents");
        setLoading(false);
      },
    });
  }, [fetchUnprocessedDocs, page, pageSize, debouncedSearch]);

  useEffect(() => {
    loadUnprocessedDocs();
  }, [loadUnprocessedDocs]);

  useEffect(() => {
    const handler = setTimeout(() => {
      const cleanedSearch = search.replace(/\s+/g, '');
      if (cleanedSearch.length >= 3 || cleanedSearch.length === 0) {
        setDebouncedSearch(search);
      }
    }, 300);
    return () => clearTimeout(handler);
  }, [search]);

  const columns: ColumnsType<UnprocessedDoc> = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
    },
    {
      title: "Uploaded At",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (createdAt: string) => {
        const date = new Date(Number(createdAt));
        return date.toLocaleDateString() + ' ' + date.toLocaleTimeString("en-US", {
          hour12: true,
          hour: 'numeric',
          minute: '2-digit'
        });
      }
    },
    {
      title: "Filename",
      dataIndex: "filename",
      key: "filename",
      render: (filename, record) => (
        <Button type="link" onClick={() => handlePreviewClick(record.blobId, filename)}>
          {filename}
        </Button>
      )
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status: string) => STATUS_SUBSTITUTIONS[status] || status
    },
    {
      title: "Statement Date",
      dataIndex: "statementDate",
      key: "statementDate"
    },
    {
      title: "Is Bill",
      dataIndex: "isBill",
      key: "isBill",
      render: (isBill: boolean) => (isBill ? "Yes" : "No"),
    },
    {
      title: "Account #",
      dataIndex: "accountNumber",
      key: "accountNumber"
    },
    {
      title: "Customer Name",
      dataIndex: "customerName",
      key: "customerName",
    },
    {
      title: "Service Address",
      dataIndex: "serviceAddress",
      key: "serviceAddress",
    },
    {
      title: "Provider",
      dataIndex: "vendor",
      key: "vendor",
    },
    {
      title: "Amount Due",
      dataIndex: "amountDue",
      key: "amountDue",
    },
    {
      title: "Due Date",
      dataIndex: "dueDate",
      key: "dueDate",
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <div style={{ display: "flex", gap: 8 }}>
          <Button type="primary" onClick={() => handleUserClick(record.id)}>
            Reset Status
          </Button>
          {record.status === "FAILED" && (
            <Button type="primary" onClick={() => handleSortClick(record)}>
              Sort
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <h1 style={{ margin: 0 }}>Processed Docs</h1>
        <Button type="default" onClick={handleRefreshClick}>Refresh</Button>
      </div>
      <Input
        placeholder="Search..."
        value={search}
        onChange={handleSearchChange}
        style={{ width: 240, marginBottom: 8 }}
      />

      <ClientSortModal
        open={isModalVisible}
        onCancel={handleModalCancel}
        previewUrl={previewUrl}
        selectedDocId={selectedDocId}
        previewFileName={previewFileName}
        searchUserTerm={searchUserTerm}
        searchResults={searchResults}
        selectedUser={selectedUser}
        selectedAccount={selectedAccount}
        isStatementFormValid={isStatementFormValid}
        userAccountData={{ ...userAccountData, accountStatements }} // ✅ Inject statements here
        isMoving={isMoving}
        onSearchUser={(val) => {
          setSearchUserTerm(val);
          debouncedSearchUsers(val);
        }}
        onSelectUser={(user) => {
          setSelectedUser(user);
          setSelectedAccount(null);
          setAccountStatements([]); // ✅ clear old statements
        }}
        onSelectAccount={(id) => {
          const found = userAccountData?.user?.accounts?.find((a: UserAccount) => a.id === id);
          setSelectedAccount(found || null);
          if (found) {
            getAccountStatements({ variables: { accountId: Number(id) } });
          } else {
            setAccountStatements([]);
          }
        }}
        onMoveToUnsorted={handleMoveToUnsorted}
        onMoveToAccount={handleMoveToAccount}
        onStatementFormChange={handleStatementFormChange} // ✅ Added missing prop
      />


    <Modal
      title="Are you sure?"
      open={isMoveToAccountConfirmationVisible}
      onOk={performMoveToAccount}
      onCancel={() => setIsMoveToAccountConfirmationVisible(false)}
      okText="Yes, move to Account"
      cancelText="Cancel"
      confirmLoading={isMoving}
    >
      <p>Are you sure you want to move this document to the selected account?</p>
    </Modal>

      <Modal
        title="Are you sure?"
        open={isConfirmationVisible}
        onOk={performMoveToUnsorted}
        onCancel={() => setIsConfirmationVisible(false)}
        okText="Yes, move to Unsorted"
        cancelText="Cancel"
        confirmLoading={isMoving}
      >
        <p>This client has active accounts. Are you sure you want to move this document to Unsorted?</p>
      </Modal>

      {isPreviewVisible && previewUrl && (
        <Modal
          open={isPreviewVisible}
          title={previewFileName}
          footer={null}
          onCancel={() => setIsPreviewVisible(false)}
          width={800}
        >
          <iframe
            src={previewUrl}
            title={previewFileName}
            style={{ width: '100%', height: 600, border: '1px solid #ccc' }}
          />
        </Modal>
      )}
      <Spin spinning={loading}>
        <Table
          columns={columns}
          dataSource={unprocessedDocs}
          rowKey="id"
          pagination={{ current: page, pageSize: pageSize, total }}
          onChange={handleTableChange}
          scroll={{ x: 'max-content' }}
        />
      </Spin>
    </>
  );
};

export default UnprocessedDocsTable;
